/** @flow */

import http from "@http";
import { takeLatest, takeEvery, put, all, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import {
  GetReportingMiReportsMyPresentations,
  GetReportingMiReportsSharedPresentations,
  GetReportingMiReportsMiSlides,
  MiReportsCreateNewPresentation,
  MiReportsUpdatePresentation,
  GetReportingMiReportsPresentationLogs,
  MiReportsEmployeesToShareSearch,
  MiReportsSharePresentation,
  DownloadMiReportsPresentation,
} from "@modules/reporting/constants/actions";
import actionCreators from "@modules/reporting/actionCreator";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadFile } from "@sagas/utils";

type ReportRequest = $Exact<{
  clientId: number,
  filters?: Object,
  name?: string,
}>;

const BASE_URL = clientId => `api/v1/reports/client/${clientId}/mi-reports`;

function* onGetReportingMiReportsMyPresentations({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `${BASE_URL(payload.clientId)}/my-reports`);

    yield put(actionCreators.GetReportingMiReportsMyPresentationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingMiReportsMyPresentationsError(e.response));
  }
}

function* onGetReportingMiReportsSharedPresentations({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `${BASE_URL(payload.clientId)}/shared-with-me`);

    const data = {
      ...response.data,
      content: response.data.content.map((record: Object) => {
        return {
          ...record,
          id: record.id.id,
        };
      }),
    };

    yield put(actionCreators.GetReportingMiReportsSharedPresentationsSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetReportingMiReportsSharedPresentationsError(e.response));
  }
}

function* onGetReportingMiReportsMiSlides({
  payload,
}: Action<ReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, "api/v1/reports/mi-reports/slides");

    yield put(
      actionCreators.GetReportingMiReportsMiSlidesSuccess({
        data: response.data,
        selectedSlides: payload.filters && payload.filters.slides,
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetReportingMiReportsMiSlidesError(e.response));
  }
}

type CreateReportRequest = $Exact<{
  ...ReportRequest,
  filters: Object,
}>;

function* onMiReportsCreateNewPresentation({
  payload,
}: Action<CreateReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const excludeAbsenceReasons =
      payload.filters.excludeAbsenceReasons && payload.filters.excludeAbsenceReasons.length
        ? payload.filters.excludeAbsenceReasons.map(el => ({ id: el.id }))
        : [];

    const selectedUnits = payload.filters.selectedOrganisationalUnitIds
      ? payload.filters.selectedOrganisationalUnitIds.map(id => ({ id }))
      : [];
    const excludedUnits = payload.filters.excludedOrganisationalUnitIds
      ? payload.filters.excludedOrganisationalUnitIds.map(id => ({ id }))
      : [];

    const response = yield call(http.post, `${BASE_URL(payload.clientId)}`, {
      report_name: payload.name,
      selected_organisational_units: selectedUnits,
      excluded_organisational_units: excludedUnits,
      slides: payload.filters.slides,
      absence_type: payload.filters.absenceType,
      date_range: payload.filters.dateRange,
      exclude_absence_reasons: excludeAbsenceReasons,
      from_date: payload.filters.fromDate,
      to_date: payload.filters.toDate,
    });

    yield all([
      put(actionCreators.MiReportsCreateNewPresentationSuccess(response.data)),
      put(actionCreators.GetReportingMiReportsMyPresentations({ clientId: payload.clientId })),
      put(sendSuccessMessage("MI_PRESENTATION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.MiReportsCreateNewPresentationError(e.response));
  }
}

type UpdateReportRequest = $Exact<{
  ...ReportRequest,
  data: Object,
}>;

function* onMiReportsUpdatePresentation({
  payload,
}: Action<UpdateReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.data || !payload.data.id) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { data } = payload;

    const excludeAbsenceReasons =
      data.filters.excludeAbsenceReasons && data.filters.excludeAbsenceReasons.length
        ? data.filters.excludeAbsenceReasons.map(el => ({ id: el.id }))
        : [];

    const selectedUnits = data.filters.selectedOrganisationalUnitIds
      ? data.filters.selectedOrganisationalUnitIds.map(id => ({ id }))
      : [];
    const excludedUnits = data.filters.excludedOrganisationalUnitIds
      ? data.filters.excludedOrganisationalUnitIds.map(id => ({ id }))
      : [];

    const response = yield call(http.put, `${BASE_URL(payload.clientId)}/${payload.data.id}`, {
      id: +payload.data.id,
      report_name: payload.name,
      selected_organisational_units: selectedUnits,
      excluded_organisational_units: excludedUnits,
      slides: data.filters.slides,
      absence_type: data.filters.absenceType,
      date_range: data.filters.dateRange,
      exclude_absence_reasons: excludeAbsenceReasons,
      from_date: data.filters.fromDate,
      to_date: data.filters.toDate,
    });

    yield all([
      put(actionCreators.MiReportsUpdatePresentationSuccess(response.data)),
      put(actionCreators.GetReportingMiReportsMyPresentations({ clientId: payload.clientId })),
      put(sendSuccessMessage("MI_PRESENTATION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.MiReportsUpdatePresentationError(e.response));
  }
}

type PresentationLogRequest = $Exact<{
  ...ReportRequest,
  report: Object,
}>;

function* onGetReportingMiReportsPresentationLogs({
  payload,
}: Action<PresentationLogRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { report } = payload;
    const response = yield call(
      http.get,
      `${BASE_URL(payload.clientId)}/${report.id}/files/${report.presentationFile.id}/details`,
    );

    yield put(actionCreators.GetReportingMiReportsPresentationLogsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingMiReportsPresentationLogsError(e.response));
  }
}

type ReportSearchRequest = $Exact<{
  ...ReportRequest,
  search: string,
}>;

function* onMiReportsEmployeesToShareSearch({
  payload,
}: Action<ReportSearchRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          sort: "last_name,asc",
          search: `is_active:true,last_name~${payload.search}`,
        },
      },
    );

    yield put(actionCreators.MiReportsEmployeesToShareSearchSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.MiReportsEmployeesToShareSearchError(e.response));
  }
}

type ReportShareRequest = $Exact<{
  ...ReportRequest,
  report: Object,
  employees: Array<Object>,
}>;

function* onMiReportsSharePresentation({
  payload,
}: Action<ReportShareRequest>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.report ||
      !payload.employees ||
      !payload.employees.length
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { report } = payload;
    const recipients = payload.employees.map(employee => ({
      id: employee.id,
      first_name: employee.firstName,
      last_name: employee.lastName,
      employee_ref_primary: employee.employeeRefPrimary,
      workEmail: employee.contactDetails.workEmail,
      name: report.name,
    }));

    const response = yield call(
      http.post,
      `${BASE_URL(payload.clientId)}/${report.id}/files/${report.presentationFile.id}/recipient`,
      {
        recipients,
      },
    );

    yield all([
      put(actionCreators.MiReportsSharePresentationSuccess(response.data)),
      put(actionCreators.GetReportingMiReportsMyPresentations({ clientId: payload.clientId })),
      put(sendSuccessMessage("MI_PRESENTATION_SHARED")),
    ]);
  } catch (e) {
    yield put(actionCreators.MiReportsSharePresentationError(e.response));
  }
}

export function* onDownloadMiReportsPresentation({
  payload,
}: Action<{ clientId: number, reportId: number, fileId: number, fileName: string }>): Generator<
  *,
  void,
  *,
> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.reportId ||
      !payload.fileId ||
      !payload.fileName
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${BASE_URL(payload.clientId)}/${payload.reportId}/files/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadFile(response.data, `${payload.fileId} ${payload.fileName}`);

    yield put(actionCreators.DownloadMiReportsPresentationSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.DownloadMiReportsPresentationError(e.response));
  }
}

export default function* reportSaga(): Generator<*, void, void> {
  yield all([
    takeLatest(GetReportingMiReportsMyPresentations, onGetReportingMiReportsMyPresentations),
    takeLatest(
      GetReportingMiReportsSharedPresentations,
      onGetReportingMiReportsSharedPresentations,
    ),
    takeLatest(GetReportingMiReportsMiSlides, onGetReportingMiReportsMiSlides),
    takeLatest(MiReportsCreateNewPresentation, onMiReportsCreateNewPresentation),
    takeLatest(MiReportsUpdatePresentation, onMiReportsUpdatePresentation),
    takeLatest(GetReportingMiReportsPresentationLogs, onGetReportingMiReportsPresentationLogs),
    takeEvery(MiReportsEmployeesToShareSearch, onMiReportsEmployeesToShareSearch),
    takeLatest(MiReportsSharePresentation, onMiReportsSharePresentation),
    takeLatest(DownloadMiReportsPresentation, onDownloadMiReportsPresentation),
  ]);
}
